import { useNavigate } from '@tanstack/react-router';
import GentlyTable from 'src/components/common/GentlyTable';
import { TableColumn } from 'src/components/common/PaginatedGentlyTable';
import { RebateAgreementListItem } from 'src/api/rebates/types';
import useRebateAgreements from 'src/api/rebates/useRebateAgreements';

interface SupplierRebateAgreementsTabProps {
  supplierId: string;
}

export function SupplierRebateAgreementsTab({
  supplierId,
}: SupplierRebateAgreementsTabProps) {
  const navigate = useNavigate();

  const { data: rebateAgreementsData, isLoading: rebateAgreementsLoading } =
    useRebateAgreements({ supplierId }, !!supplierId);

  const handleAgreementClick = (agreement: RebateAgreementListItem) => {
    navigate({ to: '/rebates/agreements/$id', params: { id: agreement.id } });
  };

  // Rebate Agreements table columns
  const rebateAgreementColumns: TableColumn<RebateAgreementListItem>[] = [
    {
      id: 'name',
      label: 'Agreement Name',
      sortable: true,
      render: (value) => (
        <span className="font-medium text-sm block truncate" title={value}>
          {value}
        </span>
      ),
    },
    {
      id: 'start_date',
      label: 'Start Date',
      sortable: true,
      render: (value) => (
        <span className="text-sm whitespace-nowrap">
          {new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC',
          }).format(new Date(value as string))}
        </span>
      ),
    },
    {
      id: 'end_date',
      label: 'End Date',
      sortable: true,
      render: (value) => (
        <span className="text-sm whitespace-nowrap">
          {new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC',
          }).format(new Date(value as string))}
        </span>
      ),
    },
    {
      id: 'created_at',
      label: 'Created',
      sortable: true,
      render: (value) => (
        <span className="text-sm whitespace-nowrap">
          {new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC',
          }).format(new Date(value as string))}
        </span>
      ),
    },
  ];

  return (
    <div className="p-6">
      <h2 className="text-xl font-medium mb-6">Rebate Agreements</h2>

      <div className="w-full overflow-x-auto">
        <GentlyTable
          data={rebateAgreementsData || []}
          columns={rebateAgreementColumns}
          searchFields={['name']}
          searchPlaceholder="Search agreements..."
          emptyMessage="No rebate agreements found for this supplier"
          isLoading={rebateAgreementsLoading}
          onRowClick={handleAgreementClick}
        />
      </div>
    </div>
  );
}
